import { AfterViewInit, Component, Inject, Injector, OnInit, Optional, ChangeDetectionStrategy } from '@angular/core';
import { SamplingEquipment } from 'src/app/models/samplingEquipment.model';
import { SamplingEquipmentService } from 'src/app/services/sampling-equipment.service';
import { CoreGridComponent } from 'src/app/shared/grid/core-grid/core-grid.component';
import { CommonService } from 'src/app/services/common.service';
import { ToastService } from 'src/app/services/toast.service';
import { AuthService } from 'src/app/services/auth.service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { getFilteredDevicesRouteConfig } from 'src/app/shared/constants/devicesRouteConfig.constants';
import { Observable, of, forkJoin } from 'rxjs';
import { ColumnConfig } from 'src/app/shared/grid/core-grid/columnConfig.model';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { IFormDialogData } from 'src/app/shared/form/form.component';
import { SamplingEquipmentDialogComponent } from 'src/app/modals/sampling-equipment-dialog/sampling-equipment-dialog.component';
import { GridViewService } from 'src/app/services/grid-view.service';
import { GridSettingsService } from 'src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service';
import { EquipmentStatus } from 'src/app/enums/equipmentStatus.enum';
import { SampleTypeEnum } from 'src/app/enums/sampleType.enum';
import { UserService } from 'src/app/services/user.service';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { Lookup } from 'src/app/models/lookup.model';
import { ISearchFieldData } from 'src/app/shared/grid/baseGrid.component';
import { splitCamelCase } from 'src/app/_helpers/format-text';
import { TranslateService } from 'src/app/services/translate.service';

@Component({
  standalone: false,
  selector: 'app-sampling-equipment',
  templateUrl: './../../shared/grid/core-grid/core-grid.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./../../shared/grid/core-grid/core-grid.component.scss'],
})
export class SamplingEquipmentComponent extends CoreGridComponent<SamplingEquipment> implements OnInit, AfterViewInit {

  organizationLevels: Lookup[] = [];
  users: Lookup[] = [];

  constructor(
    samplingEquipmentService: SamplingEquipmentService,
    toastService: ToastService,
    dialog: MatDialog,
    commonService: CommonService,
    authService: AuthService,
    gridViewService: GridViewService,
    gridSettingsService: GridSettingsService,
    private userService: UserService,
    private organizationLevelService: OrganizationLevelService,
    private dateFormatService: DateFormatService,
    private translateService: TranslateService,
    protected injector: Injector,
    @Optional() @Inject(MAT_DIALOG_DATA) public searchFieldData: ISearchFieldData
  ) {
    super(toastService, dialog, commonService, authService, gridViewService, gridSettingsService);
    this.gridService = samplingEquipmentService;
  }

  protected get objectId(): string {
    return RolePermissionObjects.SamplingEquipment;
  }

  protected override get isGridViewEnabled(): boolean { return true; }

  get hasOrgFilter(): boolean {
    return true;
  }

  ngOnInit(): void {
    this.searchDialogRef = this.injector.get(MatDialogRef<SamplingEquipmentComponent>, null);
    super.ngOnInit();
    forkJoin({
      organizationLevels: this.organizationLevelService.getAll(),
      users: this.userService.getAll()
    }).subscribe(({ organizationLevels, users }) => {
      this.organizationLevels = organizationLevels;
      this.users = users;
    });
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.loadHeaderData();
  }

  override initializeColumns(): void {
    this.columnConfigs = [
      new ColumnConfig<SamplingEquipment>({
        name: nameOf<SamplingEquipment>(x => x.modelNumber),
        displayName: 'Model Number',
        cellTemplate: (entity) => entity.modelNumber || '-',
      }),
      new ColumnConfig<SamplingEquipment>({
        name: nameOf<SamplingEquipment>(x => x.serialNumber),
        displayName: 'Serial Number',
        cellTemplate: (entity) => entity.serialNumber || '-',
      }),
      new ColumnConfig<SamplingEquipment>({
        name: nameOf<SamplingEquipment>(x => x.manufacturer),
        displayName: 'Manufacturer',
        cellTemplate: (entity) => entity.manufacturer || '-',
      }),
      new ColumnConfig<SamplingEquipment>({
        name: nameOf<SamplingEquipment>(x => x.sensorId),
        displayName: 'Sensor Id',
        cellTemplate: (entity) => entity.sensorId || '-',
      }),
      new ColumnConfig<SamplingEquipment>({
        name: nameOf<SamplingEquipment>(x => x.dateAcquired),
        displayName: 'Date Acquired',
        cellTemplate: (entity) => entity.dateAcquired ? this.dateFormatService.convertUTCDateStringToUserTimeZoneString(entity.dateAcquired) : '-',
      }),
      new ColumnConfig<SamplingEquipment>({
        name: nameOf<SamplingEquipment>(x => x.status),
        displayName: 'Status',
        cellTemplate: (e) => e.status != null ? this.translateService.getDynamicTranslation(splitCamelCase(EquipmentStatus[e.status])) : '-',
        enumFilterMap: this.enumToValueNameArray(EquipmentStatus)
      }),
      new ColumnConfig<SamplingEquipment>({
        name: nameOf<SamplingEquipment>(x => x.createdByUserId),
        displayName: 'Created By',
        cellTemplate: (e) => this.users?.find(u => u.id?.toString() === e.createdByUserId)?.name || '-'
      }),
      new ColumnConfig<SamplingEquipment>({
        name: nameOf<SamplingEquipment>(x => x.organizationLevelId),
        displayName: 'Visibility',
        cellTemplate: (e) => this.organizationLevels?.find(org => org.id === e.organizationLevelId)?.name || '-'
      }),
      new ColumnConfig<SamplingEquipment>({
        name: nameOf<SamplingEquipment>(x => x.sampleType),
        displayName: 'Sample Type',
        cellTemplate: (e) => e.sampleType != null ? this.translateService.getDynamicTranslation(SampleTypeEnum[e.sampleType]) : '-',
        enumFilterMap: this.enumToValueNameArray(SampleTypeEnum)
      }),
      new ColumnConfig<SamplingEquipment>({
        name: nameOf<SamplingEquipment>(x => x.responsibleUserId),
        displayName: 'Responsible User',
        cellTemplate: (e) => this.users?.find(u => u.id?.toString() === e.responsibleUserId)?.name || '-'
      }),
      new ColumnConfig<SamplingEquipment>({
        name: nameOf<SamplingEquipment>(x => x.isDeleted),
        displayName: 'Deleted',
        cellTemplate: (e) => this.translateService.getDynamicTranslation(e.isDeleted ? 'Yes' : 'No'),
      }),
      new ColumnConfig<SamplingEquipment>({
        name: nameOf<SamplingEquipment>(x => x.deletedByUserId),
        displayName: 'Deleted By',
        cellTemplate: (e) => this.users?.find(u => u.id?.toString() === e.deletedByUserId)?.name || '-'
      }),
      new ColumnConfig<SamplingEquipment>({
        name: nameOf<SamplingEquipment>(x => x.deletedDate),
        displayName: 'Deleted Date',
        cellTemplate: (entity) => entity.deletedDate ? this.dateFormatService.convertUTCDateStringToUserTimeZoneString(entity.deletedDate) : '-',
      }),
      new ColumnConfig<SamplingEquipment>({
        name: nameOf<SamplingEquipment>(x => x.equipmentCode),
        displayName: 'Equipment Code'
      }),
      new ColumnConfig<SamplingEquipment>({
        name: nameOf<SamplingEquipment>(x => x.id),
        displayName: 'Id'
      }),
      { name: 'action', displayName: 'Action' }
    ]
  }

  override objectFilterMap(propertyName: string): any | undefined {
    switch (propertyName) {
      case nameOf<SamplingEquipment>(x => x.createdByUserId): return this.userService;
      case nameOf<SamplingEquipment>(x => x.organizationLevelId): return this.organizationLevelService;
      case nameOf<SamplingEquipment>(x => x.responsibleUserId): return this.userService;
      case nameOf<SamplingEquipment>(x => x.deletedByUserId): return this.userService;
      default: return undefined;
    }
  }

  override initializeOverviewConfigs(): void {
    this.overviewConfigs = [];
  }

  override initializeTabs(): { routerLink: string; title: string }[] {
    const currentUser = this.authService.getCurrentUser();
    return getFilteredDevicesRouteConfig(currentUser.organizationLevelPermissions);
  }

  override initializeProperties(): void {
    this.addEntityTitle = 'Sampling Equipment';
    this.searchPlaceholder = 'Search Sampling Equipment';
  }

  override getOverviewData(): Observable<void> {
    return of(undefined);
  }

  override openEntityDialog(entityId?: string): void {
    const samplingEquipmentModel: IFormDialogData = {
      id: entityId
    };

    this.openDialog(SamplingEquipmentDialogComponent, samplingEquipmentModel);
  }

  override openEntityDeleteDialog(entity: SamplingEquipment): void {
    return;
  }
}